import { Injectable, inject } from '@angular/core';
import { BaseService, RequestOptions } from 'src/app/services/base.service';
import { Observable, shareReplay } from 'rxjs';
import { TaskData, TaskDataFilter } from '../stock/stock.service';
import { BookmarkData } from 'src/app/models/Bookmark';

@Injectable({
	providedIn: 'root'
})
export class TaskService {

	private static readonly REQUEST_TOP_PATH: string = "task";

	private _baseService: BaseService = inject(BaseService);

	private taskpriorities$: Observable<any>;
	private taskStatuses$: Observable<any>;
	private taskGroups$: Observable<any>;
	private userList$: Observable<any>;

	public getCoreTasks(requestOptions?: RequestOptions): Observable<Task[]> {
		const requestEndpoint: string = "GetCoreTasks";

		let url = `${TaskService.REQUEST_TOP_PATH}/${requestEndpoint}`;

		return this._baseService.get(
			url,
			requestOptions
		);
	}

	public getCoreTaskById(id: number, requestOptions?: RequestOptions): Observable<Task> {
		const requestEndpoint: string = "GetCoreTaskById";

		let url = `${TaskService.REQUEST_TOP_PATH}/${requestEndpoint}?id=${id}`;

		return this._baseService.get(
			url,
			requestOptions
		);
	}

	public getDynamicCoreTasks(requestOptions?: RequestOptions): Observable<any> {
		const requestEndpoint: string = "GetDynamicCoreTasks";

		let url = `${TaskService.REQUEST_TOP_PATH}/${requestEndpoint}`;

		return this._baseService.get(
			url,
			requestOptions
		);
	}

	public getDynamicCoreTasksForUrl(taskUrl: string, requestOptions?: RequestOptions): Observable<any> {
		const requestEndpoint: string = "GetTasksForUrlDynamic";

		let url = `${TaskService.REQUEST_TOP_PATH}/${requestEndpoint}`;

		return this._baseService.post(
			url,
			{
				taskUrl: taskUrl
			},
			requestOptions
		);
	}

	getTaskPriorities(requestOptions?: RequestOptions): Observable<TaskSelectValue[]> {
		if (!this.taskpriorities$) {
		const url = `task/GetCoreTaskPriorities`;
		this.taskpriorities$=this._baseService.get(url,requestOptions).pipe(shareReplay(1));
		}
		return this.taskpriorities$;
	}

	getTasksForUrl(taskUrl: string): Observable<Task[]> {
		const url = `task/GetTasksForUrlNew`;
		return this._baseService.post(url, {taskUrl: taskUrl} );
	}

	getTaskStatuses(requestOptions?: RequestOptions): Observable<TaskSelectValue[]> {
		if (!this.taskStatuses$) {
		const url = `task/GetCoreTaskStatuses`;
		this.taskStatuses$=this._baseService.get(url,requestOptions).pipe(shareReplay(1));
		}
		return this.taskStatuses$;

	}

	getTaskGroups(requestOptions?: RequestOptions): Observable<TaskSelectValue[]> {
		if (!this.taskGroups$) {
		const url = `task/GetCoreTaskGroups`;
		this.taskGroups$=this._baseService.get(url,requestOptions).pipe(shareReplay(1));
		}
		return this.taskGroups$;
	}

	//TODO: Remove this and replace dependencies with methods in user.service.
	public getUserList(requestOptions?: RequestOptions): Observable<string[]> {
		if (!this.userList$) {
		const url = `task/GetUserList`;

		this.userList$ = this._baseService.get(
			url,
			requestOptions
		).pipe(shareReplay(1));
		}
		return this.userList$;
	}

	createNewTask(data: Task): Observable<any> {
		console.log("createNewTask - task:", data);

		const url = `task/CreateNewTask`;
		return this._baseService.post(url, data);
	}

	updateTask(data: Task): Observable<any> {
		const url = `task/UpdateTask`;
		return this._baseService.post(url, data);
	}

	public updateMultipleTasks(data: Task[], requestOptions?: RequestOptions): Observable<any> {
		const requestEndpoint: string = "UpdateMultipleTasks";

		let url = `${TaskService.REQUEST_TOP_PATH}/${requestEndpoint}`;

		return this._baseService.post(
			url,
			data,
			requestOptions
		);
	}

	public deleteTasks(data: Task[], requestOptions?: RequestOptions): Observable<any> {
		const requestEndpoint: string = "DeleteTasks";

		let url = `${TaskService.REQUEST_TOP_PATH}/${requestEndpoint}`;

		return this._baseService.post(
			url,
			data,
			requestOptions
		);
	}

	public deleteTask(data: Task, requestOptions?: RequestOptions): Observable<any> {
		const requestEndpoint: string = "DeleteTask";

		let url = `${TaskService.REQUEST_TOP_PATH}/${requestEndpoint}`;

		return this._baseService.post(
			url,
			data,
			requestOptions
		);
	}

	SelectTaskFilterValues(id: string): Observable<TaskDataFilter[]> {
		const url = `task/SelectTaskFilterValues?id=` + id;
		return this._baseService.post(url);
	}

	LoadBookmarkFilter(data: BookmarkData): Observable<TaskDataFilter[]> {
		const url = `Settings/LoadBookmarkFilter`;
		return this._baseService.post(url, data);
	}

	LoadBookmarkData(data: BookmarkData): Observable<any> {
		const url = `Settings/LoadBookmarkData`;
		return this._baseService.post(url, data);
	}

	LoadBookmarkDataRequest(data: BookmarkData,requst: RequestOptions): Observable<any> {
		const url = `Settings/LoadBookmarkData`;
		return this._baseService.post(url, data,requst);
	}
}


export class Task {
	taskId: number;
	taskName: string;
	taskDescription: string;
	responsibleUser: string;
	createdDate: Date | number | string;
	startDate: Date | number | string;
	deadlineDate: Date | number | string;
	priority: string;
	allowReminder: boolean;
	link: string;
	solutionDescription: string;
	taskGroup1: string;
	taskGroup2: string;
	taskGroup3: string;
	taskGroup4: string;
	taskGroup5: string;
	parentTaskId: number;
	progress: number = 0;
	projectId: number;
	projectName: string;
	status: string;
	taskFilterData: TaskData[];
	isActive: boolean;

	newIdToReplaceAutogeneratedGUID: number = 0;

	public copyFromTask(task: Task) {
		this.taskName = task.taskName;
		this.taskDescription = task.taskDescription;
		this.responsibleUser = task.responsibleUser;
		this.startDate = task.startDate;
		this.deadlineDate = task.deadlineDate;
		this.priority = task.priority;
		this.allowReminder = task.allowReminder;
		this.link = task.link;
		this.solutionDescription = task.solutionDescription;
		this.taskGroup1 = task.taskGroup1;
		this.taskGroup2 = task.taskGroup2;
		this.taskGroup3 = task.taskGroup3;
		this.taskGroup4 = task.taskGroup4;
		this.taskGroup5 = task.taskGroup5;
		this.parentTaskId = task.parentTaskId;
		this.progress = task.progress;
		this.projectId = task.projectId;
		this.status = task.status;
		this.taskFilterData = task.taskFilterData;

		this.newIdToReplaceAutogeneratedGUID = task.newIdToReplaceAutogeneratedGUID;
	}

	public prepareForUpdate(tasks: Task[]): void {
		if (this.newIdToReplaceAutogeneratedGUID > 0) {
			this.taskId = this.newIdToReplaceAutogeneratedGUID;
		}

		console.log(`prepareForUpdate | Task: ${this.taskName}, Id: ${this.taskId}`);

		let taskParentIndex: number = tasks.findIndex((innerTask) => innerTask.taskId === this.parentTaskId);

		if (taskParentIndex >= 0 && tasks[taskParentIndex].newIdToReplaceAutogeneratedGUID > 0) {
			this.parentTaskId = tasks[taskParentIndex].newIdToReplaceAutogeneratedGUID;
		}
	}
}

export class CoreTaskDataField {
	fieldName: string;
	caption: string;
	isDropdown: boolean;
	dataSource: string;
}

export class TaskSelectValue {
	listId: number;
	listElement: string;
	isDefault: boolean;
}

export enum TaskGroupIdsEnum {
	TaskGroup1 = 3,
	TaskGroup2 = 4
}
